import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CallIcon from "@mui/icons-material/Call";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useNavigate } from "react-router-dom";
import type { OrganisationType } from "../../../../clients/graphqlTypes.js";
import type { OnboardingFlowStage } from "./OnboardingFlowModal.js";
import { OnboardingDoneIllustration } from "./OnboardingIllustrations.js";
import {
  ONBOARDING_BUTTON_HEIGHT,
  ONBOARDING_BUTTON_LABEL,
  ONBOARDING_COLORS,
} from "./onboardingDesign.js";
import { OnboardingStatusLabel } from "./OnboardingStatusLabel.js";

interface Props {
  organisationType: OrganisationType;
  hasCallAssistant: boolean;
  /** The property owner saved the "Your property" step (not skipped it). */
  propertySaved?: boolean;
  setStage: (newStage: OnboardingFlowStage | null) => void;
}

/**
 * The done step, shown as the content of a centred card over the dimmed
 * dashboard (the dialog supplies the card and the dim layer). It only
 * confirms; it claims nothing beyond what the wizard saved.
 */
export const OnboardingFlowSuccessStage = ({
  organisationType,
  hasCallAssistant,
  propertySaved = false,
  setStage,
}: Props) => {
  const { t } = useTranslate(["Global", "OrganisationProfile"]);
  const navigate = useNavigate();
  const titleId = React.useId();

  return (
    <Stack
      component="section"
      aria-labelledby={titleId}
      sx={{
        alignItems: "center",
        // Full screen below `md` (the dialog's paper is a flex column): the
        // content is centred in it instead of sticking to the top. The desktop
        // card is as tall as its content.
        flexGrow: { xs: 1, md: 0 },
        justifyContent: { xs: "center", md: "flex-start" },
        textAlign: "center",
        boxSizing: "border-box",
        px: { xs: 3, sm: "48px" },
        pt: { xs: 3, sm: "40px" },
        pb: { xs: 3, sm: "44px" },
      }}
    >
      <OnboardingDoneIllustration />
      <Box sx={{ mt: "12px" }}>
        <OnboardingStatusLabel>
          {t("Setup done", { ns: "OrganisationProfile" })}
        </OnboardingStatusLabel>
      </Box>
      <Typography
        id={titleId}
        component="h1"
        sx={{
          mt: "14px",
          fontSize: { xs: 28, sm: 34 },
          fontWeight: 500,
          lineHeight: 1.15,
          letterSpacing: "-0.015em",
          color: ONBOARDING_COLORS.heading,
        }}
      >
        {t("Meister Systems is ready", { ns: "OrganisationProfile" })}
      </Typography>
      <Typography
        sx={{
          mt: "14px",
          maxWidth: 480,
          fontSize: 17,
          lineHeight: 1.5,
          letterSpacing: "normal",
          color: ONBOARDING_COLORS.body,
        }}
      >
        {organisationType === "CRAFTSMAN"
          ? t(
              "Head to your dashboard to get started. You can change your details and services any time in Settings.",
              { ns: "OrganisationProfile" }
            )
          : propertySaved
            ? t(
                "Your property is set up. Describe your first project – or wait for your craftsman’s invitation.",
                { ns: "OrganisationProfile" }
              )
            : t(
                "Describe your first project – or wait for your craftsman’s invitation.",
                { ns: "OrganisationProfile" }
              )}
      </Typography>
      {hasCallAssistant && (
        <Stack
          direction="row"
          spacing={2}
          sx={{
            mt: 3,
            width: "100%",
            alignItems: "center",
            p: 2,
            border: `1px solid ${ONBOARDING_COLORS.border}`,
            borderRadius: "10px",
            textAlign: "left",
          }}
        >
          <CallIcon color="primary" aria-hidden />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ fontWeight: 500, color: ONBOARDING_COLORS.heading }}
            >
              {t("Hi, I'm AI-Paul!", { ns: "OrganisationProfile" })}
            </Typography>
            <Typography sx={{ fontSize: 14, color: ONBOARDING_COLORS.body }}>
              {t("Your AI phone assistant.", {
                ns: "OrganisationProfile",
              })}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate("/callassistant/onboarding")}
            sx={{
              height: ONBOARDING_BUTTON_HEIGHT,
              flexShrink: 0,
              ...ONBOARDING_BUTTON_LABEL,
            }}
          >
            {t("Set up now", { ns: "OrganisationProfile" })}
          </Button>
        </Stack>
      )}
      <Button
        variant="contained"
        color="primary"
        endIcon={<ArrowForwardIcon />}
        id="go-to-dashboard"
        data-testid="go-to-dashboard"
        onClick={() => setStage(null)}
        sx={{
          mt: "32px",
          height: ONBOARDING_BUTTON_HEIGHT,
          px: "22px",
          ...ONBOARDING_BUTTON_LABEL,
        }}
      >
        {t("Go to dashboard", { ns: "OrganisationProfile" })}
      </Button>
    </Stack>
  );
};
